import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react'; // Added useCallback
import {
  Card,
  Collapse,
  IconButton,
  Grid,
  Divider,
  TableCell,
  TableRow,
  Table,
  TableHead,
  TableContainer,
  TableBody,
  Paper,
  Box,
  CircularProgress,
  TablePagination,
  Button,
  TextField,
} from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import companyInfo from 'context/companyInfo';
import { UserContext } from 'context/userContext';
import { getAllProductsFromDb, deployProducts } from 'services/product'; // deployProducts was imported but not used
import ProductExpandableTable from './components/ProductExpandableTable/';
import Icon from '@mui/material/Icon';
import { grey, green, blue } from '@mui/material/colors';
import ProductComparison from './components/ProductComparison';
import ProductDetailsView from './components/ProductDetailsView';
// import { deployProducts } from 'services/product'; // Already imported
import { OrgContext } from 'context/orgContext';
import ArgonSelect from 'components/ArgonSelect';
import { userOrgs } from 'services/org';
import { useNotifications } from 'context/NotificationContext'; // Import useNotifications

function AllProducts() {
  const { userId } = useContext(UserContext);
  const { orgs, setOrgs } = useContext(OrgContext);
  const { addNotification } = useNotifications(); // Get addNotification

  const [productsData, setProductsData] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [topLevelPage, setTopLevelPage] = useState(0);
  const [topLevelRowsPerPage, setTopLevelRowsPerPage] = useState(8);
  const [innerPage, setInnerPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [isRevenue, setRevenue] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailsView, setShowDetailsView] = useState(false);
  const [selectedDetailProduct, setSelectedDetailProduct] = useState(null);
  const [selectedDetailVersionProducts, setSelectedDetailVersionProducts] = useState(null);

  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedProductOrg, setSelectedProductOrg] = useState(null);

  const [productOptions, setProductOptions] = useState(null);
  const [orgOptions, setOrgOptions] = useState(null);

  const fetchAllData = useCallback(async () => {
    if (!userId) {
      setProductsData([]);
      setOrgs([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    addNotification({ title: ['Loading Data'], details: 'Fetching products and organizations...', severity: 'info' });

    try {
      const [productsResponse, orgsResponse] = await Promise.all([getAllProductsFromDb(), userOrgs()]);

      console.log('[productsResponse]', productsResponse);
      console.log('[orgsResponse]', orgsResponse);

      if (productsResponse.error) {
        addNotification({
          title: ['Error Fetching Products'],
          details: productsResponse.msg || 'Could not retrieve product list.',
          severity: 'error',
        });
        setProductsData([]);
      } else if (productsResponse?.products) {
        setProductsData(productsResponse.products);
      } else {
        setProductsData([]); // Ensure it's an array
      }

      console.log('org response', orgsResponse);

      if (orgsResponse.error) {
        addNotification({
          title: ['Error Fetching Orgs'],
          details: orgsResponse.msg || 'Could not retrieve organization list.',
          severity: 'error',
        });
        setOrgs([]);
      } else if (orgsResponse?.orgs) {
        setOrgs(orgsResponse.orgs);
      } else {
        setOrgs([]); // Ensure it's an array
      }
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
      addNotification({
        title: ['Network Error'],
        details: 'A problem occurred while fetching data. Please check your connection and try again.',
        severity: 'error',
      });
      setProductsData([]);
      setOrgs([]);
    } finally {
      setLoading(false);
    }
  }, [userId, addNotification, setOrgs]); // setOrgs from OrgContext

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    // This effect processes already fetched data, so no API calls here
    try {
      if (selectedDetailProduct && Array.isArray(productsData) && productsData.length > 0) {
        const productGroup = productsData.find(
          (prod) =>
            prod.groupedBy.id === selectedDetailProduct.sf_id || prod.groupedBy.id === selectedDetailProduct.groupedById
        );
        if (productGroup?.products) {
          setSelectedDetailVersionProducts(productGroup.products);
          const mapProducts = productGroup.products.map((product) => ({
            label: product.product_version,
            value: product.product_version,
          }));
          setProductOptions(mapProducts);
        } else {
          setProductOptions([]);
          setSelectedDetailVersionProducts([]);
        }
      } else {
        setProductOptions([]);
        setSelectedDetailVersionProducts([]);
      }

      if (Array.isArray(orgs)) {
        const mapOrgs = orgs.map((org) => ({
          label: org.name,
          value: org.instance_url, // Assuming instance_url is unique and used as value
          orgData: org, // Keep full org data for deployment
        }));
        setOrgOptions(mapOrgs);
      } else {
        setOrgOptions([]);
      }
    } catch (error) {
      console.error('Failed to process data for dropdown options:', error);
      addNotification({
        title: ['UI Error'],
        details: 'Could not prepare dropdown options.',
        severity: 'warning',
      });
      setProductOptions([]);
      setOrgOptions([]);
    }
  }, [productsData, orgs, selectedDetailProduct, addNotification]);

  const filteredProductsData = useMemo(() => {
    if (!searchTerm) {
      return productsData;
    }
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    return productsData.filter((group) => group.groupedBy.name.toLowerCase().includes(lowerCaseSearchTerm));
  }, [productsData, searchTerm]);

  const handleRowClick = (rowId) => {
    setExpandedRow((prevExpandedRow) => (prevExpandedRow === rowId ? null : rowId));
    if (expandedRow !== rowId) {
      setInnerPage(0);
    }
  };

  const handleTopLevelPageChange = (event, newPage) => {
    setTopLevelPage(newPage);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setTopLevelPage(0);
  };

  const columns = [
    { name: 'name', align: 'left' },
    { name: 'actions', align: 'right', width: '50px' },
  ];

  const startIndex = topLevelPage * topLevelRowsPerPage;
  const endIndex = startIndex + topLevelRowsPerPage;
  const displayedGroups = filteredProductsData.slice(startIndex, endIndex);

  const rows = useMemo(() => {
    return displayedGroups?.map((group) => ({
      // --- APPLY THE FIX HERE ---
      // The grouping key is now `group._id`.
      // The ID is `group._id.external_id` (or whatever you grouped by).
      // The name is `group._id.name`.
      id: group._id.external_id,
      name: (
        <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
          {group._id.name}
        </ArgonTypography>
      ),
      onClick: () => handleRowClick(group._id.external_id), // Use the same ID for the click handler
      selected: expandedRow === group._id.external_id,
      actions: (
        <IconButton
          aria-label="expand row"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(group._id.external_id);
          }}
        >
          <Icon>{expandedRow === group._id.external_id ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
        </IconButton>
      ),
    }));
  }, [displayedGroups, expandedRow]);

  const handleProductSelect = (product, org) => {
    setSelectedProducts((prevSelected) => {
      const isAlreadySelected = prevSelected.some((p) => p._id === product._id);

      if (org?.type) {
        // Check if org and org.type exist
        setRevenue(org.type === 'RC');
      }

      if (isAlreadySelected) {
        setSelectedProductOrg(org);
        return prevSelected.filter((p) => p._id !== product._id);
      }

      const newSelected = [product, ...prevSelected];
      setSelectedProductOrg(org);

      if (newSelected.length > 1) {
        const firstProductId = newSelected[0].groupedById;
        if (product.groupedById !== firstProductId) {
          addNotification({
            title: ['Selection Error'],
            details: 'Comparison is only allowed within the same product group. Selection reset.',
            severity: 'warning',
          });
          return [product];
        }
      }

      if (newSelected.length > 2) {
        newSelected.pop(); // Remove the oldest selection
      }
      return newSelected;
    });
  };

  const handleCompareClick = () => {
    if (selectedProducts.length === 2) {
      if (selectedProducts[0].groupedById === selectedProducts[1].groupedById) {
        setShowComparison(true);
        addNotification({
          title: ['Comparing Products'],
          details: 'Displaying product comparison view.',
          severity: 'info',
        });
      } else {
        addNotification({
          title: ['Comparison Error'],
          details: 'Please select 2 products from the *same* group to compare.',
          severity: 'warning',
        });
        setSelectedProducts([]);
      }
    } else {
      addNotification({
        title: ['Selection Required'],
        details: 'Please select exactly 2 products from the same group to compare.',
        severity: 'warning',
      });
    }
  };

  const handleBackClick = () => {
    setShowComparison(false);
    setShowDetailsView(false);
    setSelectedDetailProduct(null);
    setSelectedProducts([]); // Clear selections when going back
    addNotification({ title: ['Navigation'], details: 'Returned to product list view.', severity: 'info' });
  };

  const handleViewDetails = (product) => {
    console.log(orgs);
    const filterOrg = orgs.filter((org) => {
      return org.org_id === product.org_id;
    })[0];

    console.log(filterOrg);
    setSelectedDetailProduct(product);
    setSelectedProductOrg(filterOrg);
    setShowDetailsView(true);
    addNotification({
      title: ['Viewing Details', product.name],
      details: 'Displaying product details.',
      severity: 'info',
    });
  };

  const handleDeployProduct = async () => {
    if (selectedDetailProduct && selectedOrg && selectedOrg.orgData) {
      addNotification({
        title: ['Deployment Started', selectedDetailProduct.name],
        details: `Deploying version ${selectedDetailProduct.product_version} to ${selectedOrg.orgData.name}.`,
        severity: 'info',
      });
      setLoading(true); // Indicate loading state for deployment

      try {
        // Assuming deployProducts service exists and takes userId, orgData, productData
        const deployResponse = await deployProducts(userId, selectedOrg.orgData, selectedDetailProduct);

        if (deployResponse.error) {
          addNotification({
            title: ['Deployment Failed', selectedDetailProduct.name],
            details: deployResponse.msg || 'An error occurred during deployment.',
            severity: 'error',
          });
        } else {
          addNotification({
            title: ['Deployment Successful', selectedDetailProduct.name],
            details: `Version ${selectedDetailProduct.product_version} successfully deployed to ${
              selectedOrg.orgData.name
            }. ${deployResponse.msg || ''}`,
            severity: 'success',
          });
          // Optionally, refresh data or navigate away
        }
      } catch (error) {
        console.error('Error deploying product:', error);
        addNotification({
          title: ['Deployment Error', selectedDetailProduct.name],
          details: 'An unexpected error occurred during deployment. Please check logs.',
          severity: 'error',
        });
      } finally {
        setLoading(false); // Reset loading state
      }
    } else {
      addNotification({
        title: ['Deployment Error'],
        details: 'Please select both a product version and an organization to deploy to.',
        severity: 'warning',
      });
    }
  };

  const renderRowSubComponent = ({ row }) => {
    const selectedGroup = productsData.find((group) => group._id.external_id === row.id);
    if (!selectedGroup) return null;

    const innerRowsPerPage = 5;
    const groupID = selectedGroup._id.external_id;

    const org = selectedGroup.org; // This 'org' is from the product group, might be different from selectedOrg for deployment

    const tableRows = selectedGroup.products.map((product) => {
      const productWithGroupId = { ...product, groupedById: groupID };
      return {
        productName: (
          <ArgonTypography fontWeight="medium" variant="caption1" color="secondary">
            {product.name || 'Unknown Product'}
          </ArgonTypography>
        ),
        createdDate: (
          <ArgonTypography fontWeight="medium" variant="caption1" color="secondary">
            {product.created_at ? new Date(product.created_at).toLocaleString() : 'N/A'}
          </ArgonTypography>
        ),
        createdBy: (
          <ArgonTypography fontWeight="medium" variant="caption1" color="secondary">
            {product.user?.email || 'Unknown User'}
          </ArgonTypography>
        ),
        product_version: (
          <ArgonTypography fontWeight="medium" variant="caption1" color="secondary">
            {product.product_version || '-'}
          </ArgonTypography>
        ),
        actions: (
          <>
            <Button
              variant="contained" // Changed to text for less visual clutter
              color="info"
              size="small"
              style={{ marginRight: '8px' }}
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails(product);
              }}
            >
              View Details
            </Button>
            {/* Delete functionality can be added here with its own handler */}
            {/* <IconButton color="secondary" size="small" aria-label="delete" onClick={(e) => e.stopPropagation()}>
              <Icon>delete</Icon>
            </IconButton> */}
          </>
        ),
        id: product._id,
        is_active: product.is_active,
        isSelected: selectedProducts.some((p) => p._id === product._id),
        product: product,
        org: org, // Pass the org associated with this product group
      };
    });

    const subColumns = [
      {
        name: 'productName',
        label: 'Product Name',
        align: 'left',
        component: (r) => r.productName,
        sx: { fontSize: '0.875rem' },
      },
      {
        name: 'createdDate',
        label: 'Created Date',
        align: 'left',
        component: (r) => r.createdDate,
        sx: { fontSize: '0.875rem' },
      },
      {
        name: 'createdBy',
        label: 'Created By',
        align: 'left',
        component: (r) => r.createdBy,
        sx: { fontSize: '0.875rem' },
      },
      {
        name: 'product_version',
        label: 'Version',
        align: 'left',
        component: (r) => r.product_version,
        sx: { fontSize: '0.875rem' },
      },
      {
        name: 'actions',
        label: 'Actions',
        align: 'right',
        component: (r) => r.actions,
        sx: { paddingRight: '16px', fontSize: '0.875rem' },
      },
    ];

    const handleInnerPageChange = (event, newPage) => setInnerPage(newPage);
    const innerStartIndex = innerPage * innerRowsPerPage;
    const innerEndIndex = innerStartIndex + innerRowsPerPage;
    const displayedInnerRows = tableRows.slice(innerStartIndex, innerEndIndex);

    return (
      <TableRow>
        <TableCell colSpan={columns.length} style={{ padding: 0, borderBottom: 'none' }}>
          <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
            <ArgonBox p={2} bgcolor={grey[50]}>
              <TableContainer component={Paper} elevation={0} variant="outlined">
                <Table aria-label="product details table" size="small">
                  <TableHead sx={{ display: 'contents' }}>
                    <TableRow>
                      {subColumns.map((column) => (
                        <TableCell
                          key={column.name}
                          align={column.align}
                          sx={{ ...column.sx, color: grey[700], fontWeight: 'bold', backgroundColor: grey[100] }}
                        >
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedInnerRows.length > 0 ? (
                      displayedInnerRows.map((innerRow) => (
                        <TableRow
                          key={innerRow.id}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                            backgroundColor: innerRow.isSelected
                              ? blue[100]
                              : innerRow.is_active
                              ? green[50]
                              : 'inherit',
                            '&:hover': { backgroundColor: innerRow.isSelected ? blue[200] : grey[100] },
                            cursor: 'pointer',
                          }}
                          onClick={() => handleProductSelect(innerRow.product, innerRow.org)}
                        >
                          {subColumns.map((column) => (
                            <TableCell key={column.name} align={column.align} sx={column.sx}>
                              {column.component(innerRow)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={subColumns.length} align="center">
                          <ArgonTypography variant="caption" color="textSecondary" p={2}>
                            No product versions in this group.
                          </ArgonTypography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {tableRows.length > innerRowsPerPage && (
                <ArgonBox display="flex" justifyContent="center" mt={1}>
                  <TablePagination
                    rowsPerPageOptions={[innerRowsPerPage]}
                    component="div"
                    count={tableRows.length}
                    rowsPerPage={innerRowsPerPage}
                    page={innerPage}
                    onPageChange={handleInnerPageChange}
                    labelRowsPerPage="" // Hide "Rows per page" label
                    sx={{
                      '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.8rem' },
                    }}
                  />
                </ArgonBox>
              )}
            </ArgonBox>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  const handleChangeOrg = (event) => {
    const { value } = event.target; // value is instance_url
    const selectedOrgOption = orgOptions.find((orgOpt) => orgOpt.value === value);
    if (selectedOrgOption) {
      setSelectedOrg(selectedOrgOption); // selectedOrg now holds { label, value, orgData }
    } else {
      setSelectedOrg(null);
    }
  };

  const handleChangeProductVersion = (event) => {
    const { value } = event.target; // value is product_version string
    if (selectedDetailProduct && Array.isArray(selectedDetailVersionProducts)) {
      const newSelectedVersion = selectedDetailVersionProducts.find((p) => p.product_version === value);
      if (newSelectedVersion) {
        setSelectedDetailProduct(newSelectedVersion); // Update the main selected product to this version
        addNotification({
          title: ['Version Changed', newSelectedVersion.name],
          details: `Switched to version ${newSelectedVersion.product_version}.`,
          severity: 'info',
        });
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox display="flex" justifyContent="space-between" alignItems="center" p={3} flexWrap="wrap" gap={2}>
              <ArgonTypography variant="h6">
                {selectedDetailProduct
                  ? selectedDetailProduct.name
                  : selectedProducts.length > 0 && showComparison
                  ? selectedProducts[0]?.name
                  : 'Products'}
                {showDetailsView && selectedDetailProduct && productOptions && productOptions.length > 0 && (
                  <ArgonSelect
                    ml={2}
                    name="productVersion"
                    placeholder="Select Version"
                    value={selectedDetailProduct.product_version || ''}
                    onChange={handleChangeProductVersion}
                    options={productOptions}
                    sx={{ display: 'inline-flex' }}
                  />
                )}
              </ArgonTypography>
              <ArgonBox display="flex" alignItems="center" gap={1}>
                {!showDetailsView && !showComparison && (
                  <TextField
                    placeholder="Search Products"
                    variant="outlined"
                    size="small" // Changed to small
                    value={searchTerm}
                    onChange={handleSearchChange}
                    sx={{ minWidth: '250px', marginRight: '1rem' }}
                  />
                )}
                {showDetailsView ? (
                  <ArgonBox display="flex" alignItems="center" gap={1}>
                    <ArgonSelect
                      name="orgPlatform"
                      placeholder="Select Org to Deploy"
                      value={selectedOrg ? selectedOrg.value : ''}
                      onChange={handleChangeOrg}
                      options={orgOptions || []}
                      sx={{ minWidth: '200px' }}
                    />
                    <Button
                      variant="contained"
                      color="info" // Changed to primary for deploy
                      onClick={handleDeployProduct}
                      disabled={!selectedOrg || !selectedDetailProduct || loading} // Disable if loading (deploying)
                    >
                      {loading && selectedOrg ? 'Deploying...' : 'Deploy'}
                    </Button>
                  </ArgonBox>
                ) : (
                  !showComparison && ( // Hide compare button if already comparing or in details view
                    <Button
                      variant="contained"
                      color="info"
                      onClick={handleCompareClick}
                      disabled={selectedProducts.length !== 2}
                    >
                      Compare ({selectedProducts.length}/2)
                    </Button>
                  )
                )}
                {(showComparison || showDetailsView) && ( // Show back button only if in comparison or details view
                  <Button onClick={handleBackClick} variant="contained" color="info">
                    Back to List
                  </Button>
                )}
              </ArgonBox>
            </ArgonBox>
            <ArgonBox
              sx={{
                '& .MuiTableRow-root:not(:last-child)': {
                  '& td': {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading && !selectedOrg ? ( // Show main loading spinner only if not deploying
                <Card>
                  <Box display="flex" justifyContent="center" alignItems="center" p={5} minHeight="300px">
                    <CircularProgress />
                  </Box>
                </Card>
              ) : showComparison ? (
                <ProductComparison
                  org={selectedProductOrg}
                  products={selectedProducts}
                  isRevenue={isRevenue}
                  onBack={handleBackClick}
                />
              ) : showDetailsView ? (
                <ProductDetailsView
                  org={selectedProductOrg}
                  product={selectedDetailProduct}
                  allProducts={selectedDetailVersionProducts}
                  onClose={handleBackClick}
                />
              ) : filteredProductsData.length > 0 ? (
                <>
                  <ProductExpandableTable columns={columns} rows={rows} renderRowSubComponent={renderRowSubComponent} />
                  {filteredProductsData.length > topLevelRowsPerPage && (
                    <ArgonBox display="flex" justifyContent="center" mt={2} mb={1}>
                      <TablePagination
                        rowsPerPageOptions={[topLevelRowsPerPage]}
                        component="div"
                        count={filteredProductsData.length}
                        rowsPerPage={topLevelRowsPerPage}
                        page={topLevelPage}
                        onPageChange={handleTopLevelPageChange}
                        labelRowsPerPage=""
                        sx={{
                          '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.8rem' },
                        }}
                      />
                    </ArgonBox>
                  )}
                </>
              ) : (
                <ArgonBox
                  p={3}
                  textAlign="center"
                  minHeight="200px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <ArgonTypography variant="body1" color="textSecondary">
                    {searchTerm ? 'No product groups match your search.' : 'No products found.'}
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <ArgonBox mt={3}>
        <Footer company={companyInfo} links={companyInfo.links} />
      </ArgonBox>
    </DashboardLayout>
  );
}

export default AllProducts;
